<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-primary header-enhanced">
      <!-- User Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Users"
            type="gradient-info"
            :sub-title="stats.totalUsers ? stats.totalUsers.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">↗ {{ stats.userGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Active Users"
            type="gradient-success"
            :sub-title="stats.activeUsers ? stats.activeUsers.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Online</span>
              <span class="text-nowrap">Currently active</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="New This Month"
            type="gradient-warning"
            :sub-title="stats.newUsers ? stats.newUsers.toLocaleString() : 'Loading...'"
            icon="ni ni-fat-add"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">📈 Trending</span>
              <span class="text-nowrap">Monthly growth</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Premium Users"
            type="gradient-purple"
            :sub-title="stats.premiumUsers ? stats.premiumUsers.toLocaleString() : 'Loading...'"
            icon="ni ni-diamond"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">👑 Premium</span>
              <span class="text-nowrap">Paid subscribers</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-single-02 mr-2"></i>User Management
          </h3>
          <p class="text-white-50 mb-0">Manage and monitor user accounts</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button variant="light" size="sm" class="shadow-sm">
            <i class="ni ni-fat-add mr-1"></i>Add New User
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <div class="pt-2 pb-2 mb-4">
        <FilterBar @filter-set="onFilterSet" @filter-reset="onFilterReset" />
      </div>
      <b-card no-body class="table-card shadow">
        <b-card-header class="bg-gradient-info border-0">
          <b-row align-v="center">
            <b-col>
              <h5 class="mb-0 text-white">
                <i class="ni ni-bullet-list-67 mr-2"></i>User Directory
              </h5>
              <p class="text-white-50 mb-0 small">Complete user management overview</p>
            </b-col>
            <b-col class="text-right">
              <b-badge variant="light" class="mr-2">
                <i class="ni ni-single-02 mr-1"></i>{{ pagination.total }} Total
              </b-badge>
            </b-col>
          </b-row>
        </b-card-header>

        <div class="table-container">
          <el-table :data="tableData" style="width: 100%" v-loading="loading"
                    header-row-class-name="thead-light"
                    :row-class-name="tableRowClassName">
            <el-table-column label="User" min-width="180px">
              <template #default="scope">
                <div class="d-flex align-items-center">
                  <div class="user-avatar mr-3">
                    <i class="ni ni-single-02 text-primary"></i>
                  </div>
                  <div>
                    <div class="font-weight-600 text-dark">{{ scope.row.nickname }}</div>
                    <small class="text-muted">{{ scope.row.email }}</small>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="firstname" label="First Name" min-width="120px" sortable />
            <el-table-column prop="lastname" label="Last Name" min-width="120px" sortable />
            <el-table-column prop="created" label="Joined" min-width="100px" sortable>
              <template #default="scope">
                <span class="badge badge-light">{{ scope.row.created }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Status" min-width="130px">
              <template #default="scope">
                <div class="d-flex align-items-center">
                  <span v-if="scope.row.is_social == 0">
                    <b-badge v-if="scope.row.status === 1 && scope.row.activation === 1"
                            variant="success" class="mr-2">
                      <i class="ni ni-check-bold mr-1"></i>Activated
                    </b-badge>
                    <b-badge v-else-if="scope.row.status === 1 && scope.row.activation === 0"
                            variant="warning" class="mr-2">
                      <i class="ni ni-time-alarm mr-1"></i>Pending
                    </b-badge>
                    <b-badge v-else variant="danger" class="mr-2">
                      <i class="ni ni-fat-remove mr-1"></i>Deactivated
                    </b-badge>
                  </span>
                  <span v-else>
                    <b-badge v-if="scope.row.status == 1" variant="success" class="mr-2">
                      <i class="ni ni-check-bold mr-1"></i>Activated
                    </b-badge>
                    <b-badge v-else variant="danger" class="mr-2">
                      <i class="ni ni-fat-remove mr-1"></i>Deactivated
                    </b-badge>
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Actions" min-width="150px" align="center">
              <template #default="scope">
                <user-list-actions :rowData="scope.row" />
              </template>
            </el-table-column>
          </el-table>
        </div>
  
        <!-- Pagination Section -->
        <!-- <div class="d-flex align-items-center justify-content-end mt-4 mb-2 mx-4"> -->
          <b-card-footer class="py-4 d-flex justify-content-end">
            <base-pagination
            :current-page="pagination.current_page"
            :per-page="pagination.per_page"
            :total="pagination.total"
            @change="onPaginationChange"
          />
        </b-card-footer>
          
        <!-- </div> -->
      </b-card>
    </div>

    <v-dialog />
    <modals-container @modalClose="onModalClose" :style="{borderRadius: '8px'}" />
    <loading :active.sync="loading" :is-full-page="true"></loading>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment-timezone';
import FilterBar from './FilterBar.vue';
import UserListActions from './UserListActions.vue';
import baseHeader from '../../BaseHeader.vue';
import BasePagination from '../../BasePagination';
import StatsCard from '../../Cards/StatsCard.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: 'UserList',
  components: {
    FilterBar,
    UserListActions,
    baseHeader,
    BasePagination,
    StatsCard,
    Loading
  },
  data() {
    return {
      loading: false,
      tableData: [],
      pagination: {
        total: 0,
        per_page: 10,
        current_page: 1,
      },
      apiUrl: process.env.VUE_APP_ROOT_API + 'api/users',
      moreParams: {},
      stats: {
        totalUsers: null,
        activeUsers: null,
        newUsers: null,
        premiumUsers: null,
        userGrowth: 0,
      },
    };
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await axios.get(this.apiUrl, {
          params: {
            page: this.pagination.current_page,
            perPage: this.pagination.per_page,
            ...this.moreParams,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          },
        });
        this.transformData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.loading = false;
      }
    },
    transformData(data) {
      this.pagination = {
        total: data.total,
        per_page: data.perPage,
        current_page: data.currentPage,
      };
      this.tableData = data.data.map(user => ({
        ...user,
        created: moment(user.created_utc).local().format('MM/DD/YY'),
        last_activity_date: user.last_activity_date 
          ? moment(user.last_activity_date).local().format('MM/DD/YY') 
          : null,
      }));
    },
    onPaginationChange(page) {
      console.log('Página cambiada:', page); // Verifica si la función se activa y qué página se pasa
      this.pagination.current_page = page; // Actualiza la página actual
      this.fetchData(); // Vuelve a cargar los datos basados en la nueva página
    },
    onFilterSet(filterText) {
      this.moreParams = { filter: filterText };
      this.fetchData();
    },
    onFilterReset() {
      this.moreParams = {};
      this.fetchData();
    },
    onModalClose() {
      this.fetchData(); // Reload the table data after closing the modal
    },
    tableRowClassName({ row, rowIndex }) {
      return rowIndex % 2 === 0 ? 'table-row-even' : 'table-row-odd';
    },
    async fetchUserStats() {
      try {
        // Get total users count
        const totalResponse = await axios.get(this.apiUrl, {
          params: { page: 1, perPage: 1 },
          headers: { Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        });

        if (totalResponse.data && totalResponse.data.total) {
          this.stats.totalUsers = totalResponse.data.total;
        }

        // Calculate active users (users with recent activity - last 30 days)
        const thirtyDaysAgo = moment().subtract(30, 'days').format('YYYY-MM-DD');
        const activeResponse = await axios.get(this.apiUrl, {
          params: {
            page: 1,
            perPage: 1,
            filter: `last_activity_date>${thirtyDaysAgo}`
          },
          headers: { Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        });

        if (activeResponse.data && activeResponse.data.total) {
          this.stats.activeUsers = activeResponse.data.total;
        }

        // Calculate new users this month
        const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
        const newUsersResponse = await axios.get(this.apiUrl, {
          params: {
            page: 1,
            perPage: 1,
            filter: `created_utc>${startOfMonth}`
          },
          headers: { Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        });

        if (newUsersResponse.data && newUsersResponse.data.total) {
          this.stats.newUsers = newUsersResponse.data.total;
        }

        // Calculate premium users (placeholder - you can adjust based on your premium logic)
        this.stats.premiumUsers = Math.floor(this.stats.totalUsers * 0.15); // 15% premium users as example

        // Calculate growth (placeholder)
        this.stats.userGrowth = 12.5;

      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    },
  },
  mounted() {
    this.fetchData();
    this.fetchUserStats();
  },
};
</script>

<style scoped>
/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(94, 114, 228, 0.9) 0%, rgba(76, 99, 210, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Table Styles */
.table-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
}

.table-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0 0 12px 12px;
  /* padding: 0; */
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(94, 114, 228, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.table-row-even {
  background-color: rgba(0, 0, 0, 0.02);
}

.table-row-odd {
  background-color: rgba(255, 255, 255, 0.8);
}

.table-hover tbody tr:hover {
  background-color: rgba(94, 114, 228, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Badge Styles */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card-hover {
  animation: fadeInUp 0.6s ease-out;
}

.table-card {
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .table-card:hover {
    transform: none;
  }

  .table-hover tbody tr:hover {
    transform: none;
  }

  .user-avatar {
    width: 35px;
    height: 35px;
    font-size: 14px;
  }
}
</style>